import {
  AlertCircle,
  Ban,
  Filter,
  MinusCircle,
  PlusCircle,
  Search,
} from "lucide-react";
import React from "react";
import * as XLSX from "xlsx";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { useBlacklist } from "../../context/BlacklistContext";
import {
  IpBlacklistResponse,
  useIpBlacklist,
} from "../../context/IpBlacklistContext";
import { formatDate } from "../../utils/format";

interface BlacklistItem {
  phone: string;
  isBlacklisted: boolean;
  failureCount: number;
  reason?: string;
  lastStatusId?: number;
  lastOrderId?: number;
  createdAt: string;
  updatedAt: string;
}

interface BlacklistResponse {
  items: BlacklistItem[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

// IP blacklist types are provided by context

export function BlacklistPage() {
  const { getList, add, remove, bulkAdd } = useBlacklist();
  const {
    getList: getIpList,
    add: addIp,
    remove: removeIp,
    bulkAdd: bulkAddIps,
  } = useIpBlacklist();

  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(10);

  const [newPhone, setNewPhone] = React.useState("");
  const [newReason, setNewReason] = React.useState("");
  const [phoneError, setPhoneError] = React.useState<string | null>(null);
  const [bulkFile, setBulkFile] = React.useState<File | null>(null);
  const [bulkReason, setBulkReason] = React.useState("");
  const [bulkLoading, setBulkLoading] = React.useState(false);
  const [bulkSummary, setBulkSummary] = React.useState<{
    added: number;
    alreadyPresent: number;
    invalid: number;
  } | null>(null);

  const isValidPhone = React.useMemo(() => {
    return newPhone.length === 10 && newPhone.startsWith("07");
  }, [newPhone]);

  const [data, setData] = React.useState<BlacklistResponse | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);

  const refetch = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await getList({
        page,
        limit: pageSize,
        search: search || undefined,
      });
      setData(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Eroare la încărcarea datelor");
    } finally {
      setIsLoading(false);
    }
  }, [getList, page, pageSize, search]);

  React.useEffect(() => {
    refetch();
  }, [refetch]);

  const [isMutatingAdd, setIsMutatingAdd] = React.useState(false);
  const [isMutatingRemove, setIsMutatingRemove] = React.useState(false);

  const handleAdd = async () => {
    if (!newPhone.trim()) return;
    try {
      setIsMutatingAdd(true);
      await add(newPhone.trim(), newReason.trim() || undefined);
      setNewPhone("");
      setNewReason("");
      await refetch();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Eroare la adăugare");
    } finally {
      setIsMutatingAdd(false);
    }
  };

  const handleRemove = async (phone: string) => {
    if (!phone) return;
    const confirmRemove = window.confirm(
      `Sigur vrei să scoți ${phone} din blacklist?`
    );
    if (!confirmRemove) return;
    try {
      setIsMutatingRemove(true);
      await remove(phone);
      await refetch();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Eroare la eliminare");
    } finally {
      setIsMutatingRemove(false);
    }
  };

  const parsePhonesFromFile = async (file: File): Promise<string[]> => {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    const rows = XLSX.utils.sheet_to_json<any[]>(worksheet, { header: 1 });
    const phones: string[] = [];
    for (const row of rows) {
      if (!row) continue;
      const value = row[0];
      if (value === undefined || value === null) continue;
      const asString = String(value).trim();
      if (asString) phones.push(asString);
    }
    return phones;
  };

  const handleBulkImport = async () => {
    if (!bulkFile) return;
    try {
      setBulkLoading(true);
      setBulkSummary(null);
      const phones = await parsePhonesFromFile(bulkFile);
      if (!phones.length) {
        alert("Fișierul nu conține niciun număr de telefon (prima coloană)");
        return;
      }
      const summary = await bulkAdd(phones, bulkReason || undefined);
      setBulkSummary(summary);
      setBulkFile(null);
      await refetch();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Eroare necunoscută la import");
    } finally {
      setBulkLoading(false);
    }
  };

  // =============================
  // IP Blacklist State & Helpers
  // =============================
  const [ipSearch, setIpSearch] = React.useState("");
  const [ipPage, setIpPage] = React.useState(1);
  const [ipPageSize] = React.useState(10);

  const [newIp, setNewIp] = React.useState("");
  const [newIpReason, setNewIpReason] = React.useState("");
  const [ipError, setIpError] = React.useState<string | null>(null);

  const [ipBulkFile, setIpBulkFile] = React.useState<File | null>(null);
  const [ipBulkReason, setIpBulkReason] = React.useState("");
  const [ipBulkLoading, setIpBulkLoading] = React.useState(false);
  const [ipBulkSummary, setIpBulkSummary] = React.useState<{
    added: number;
    alreadyPresent: number;
    invalid: number;
  } | null>(null);

  const [ipData, setIpData] = React.useState<IpBlacklistResponse | null>(null);
  const [ipIsLoading, setIpIsLoading] = React.useState<boolean>(true);
  const [ipListError, setIpListError] = React.useState<string | null>(null);

  const [isMutatingIpAdd, setIsMutatingIpAdd] = React.useState(false);
  const [isMutatingIpRemove, setIsMutatingIpRemove] = React.useState(false);

  const isValidIPv4 = (value: string): boolean => {
    const ipv4 =
      /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
    return ipv4.test(value);
  };

  const isValidIPv6 = (value: string): boolean => {
    // Simple IPv6 validation (covers most common forms; not exhaustive)
    const ipv6 =
      /^([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^(?:[0-9a-fA-F]{1,4}:){1,7}:$|^:(?::[0-9a-fA-F]{1,4}){1,7}$/;
    return ipv6.test(value);
  };

  const isValidIp = React.useMemo(() => {
    const value = newIp.trim();
    if (!value) return false;
    return isValidIPv4(value) || isValidIPv6(value);
  }, [newIp]);

  const ipRefetch = React.useCallback(async () => {
    try {
      setIpIsLoading(true);
      setIpListError(null);
      const result = await getIpList({
        page: ipPage,
        limit: ipPageSize,
        search: ipSearch || undefined,
      });
      setIpData(result);
    } catch (e) {
      setIpListError(
        e instanceof Error ? e.message : "Eroare la încărcarea datelor IP"
      );
    } finally {
      setIpIsLoading(false);
    }
  }, [getIpList, ipPage, ipPageSize, ipSearch]);

  React.useEffect(() => {
    ipRefetch();
  }, [ipRefetch]);

  const handleIpAdd = async () => {
    if (!newIp.trim()) return;
    try {
      setIsMutatingIpAdd(true);
      await addIp(newIp.trim(), newIpReason.trim() || undefined);
      setNewIp("");
      setNewIpReason("");
      await ipRefetch();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Eroare la adăugare");
    } finally {
      setIsMutatingIpAdd(false);
    }
  };

  const handleIpRemove = async (ip: string) => {
    if (!ip) return;
    const confirmRemove = window.confirm(
      `Sigur vrei să scoți ${ip} din blacklist?`
    );
    if (!confirmRemove) return;
    try {
      setIsMutatingIpRemove(true);
      await removeIp(ip);
      await ipRefetch();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Eroare la eliminare");
    } finally {
      setIsMutatingIpRemove(false);
    }
  };

  const parseIpsFromFile = async (file: File): Promise<string[]> => {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    const rows = XLSX.utils.sheet_to_json<any[]>(worksheet, { header: 1 });
    const ips: string[] = [];
    for (const row of rows) {
      if (!row) continue;
      const value = row[0];
      if (value === undefined || value === null) continue;
      const asString = String(value).trim();
      if (asString) ips.push(asString);
    }
    return ips;
  };

  // bulkAddIps comes from context

  const handleIpBulkImport = async () => {
    if (!ipBulkFile) return;
    try {
      setIpBulkLoading(true);
      setIpBulkSummary(null);
      const ips = await parseIpsFromFile(ipBulkFile);
      if (!ips.length) {
        alert("Fișierul nu conține niciun IP (prima coloană)");
        return;
      }
      const summary = await bulkAddIps(ips, ipBulkReason || undefined);
      setIpBulkSummary(summary);
      setIpBulkFile(null);
      await ipRefetch();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Eroare necunoscută la import");
    } finally {
      setIpBulkLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-8 bg-background min-h-screen">
      {/* blacklist header */}
      <Card className="flex justify-between items-center ">
        <div className="flex items-center gap-2">
          <Ban className="w-8 h-8 text-red-600" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Blacklist</h1>
            <div className="flex items-center gap-2 mt-1">
              <AlertCircle className="w-4 h-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Gestionează numerele de telefon cu risc.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-muted-foreground" />
          <h2 className="font-semibold text-foreground">Filtre</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Caută după telefon..."
              value={search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearch(e.target.value)
              }
            />
          </div>

          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="flex flex-col gap-1">
              <Input
                type="tel"
                placeholder="Telefon (ex: 07xxxxxxxx)"
                value={newPhone}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  let value = e.target.value.replace(/\D/g, "");

                  if (value === "") {
                    setNewPhone("");
                    setPhoneError(null);
                    return;
                  }

                  if (value.length === 1 && value !== "0") {
                    value = "0" + value;
                  }

                  if (
                    value.length === 2 &&
                    value[0] === "0" &&
                    value[1] !== "7"
                  ) {
                    value = "07";
                  }

                  if (value.length > 10) {
                    value = value.slice(0, 10);
                  }

                  setNewPhone(value);
                  setPhoneError(
                    value.length === 10 && value.startsWith("07")
                      ? null
                      : "Număr invalid. Format: 07xxxxxxxx (10 cifre)"
                  );
                }}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter" && isValidPhone) handleAdd();
                }}
              />
              {phoneError && (
                <span className="text-xs text-red-600">{phoneError}</span>
              )}
            </div>
            <Input
              placeholder="Motiv (opțional)"
              value={newReason}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNewReason(e.target.value)
              }
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter" && isValidPhone) handleAdd();
              }}
            />
            <Button
              className="flex items-center gap-2"
              onClick={handleAdd}
              disabled={isMutatingAdd || !isValidPhone}
            >
              <PlusCircle className="w-4 h-4" />
              Adaugă în blacklist
            </Button>
          </div>
        </div>
      </Card>

      {/* Bulk Import */}
      <Card className="p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-muted-foreground" />
          <h2 className="font-semibold text-foreground">
            Import din Excel (prima coloană = telefon)
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
          <div className="flex flex-col gap-1">
            <Input
              type="file"
              placeholder="Importă din Excel"
              className="flex items-center gap-2 text-muted-foreground "
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
            />
            {bulkFile && (
              <span className="text-xs text-muted-foreground">
                {bulkFile.name}
              </span>
            )}
          </div>
          <Input
            placeholder="Motiv pentru toate (opțional)"
            value={bulkReason}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setBulkReason(e.target.value)
            }
          />
          <Button
            className="flex items-center gap-2 "
            onClick={handleBulkImport}
            disabled={bulkLoading || !bulkFile}
          >
            {bulkLoading ? "Se procesează..." : "Importă din Excel"}
          </Button>
        </div>
        {bulkSummary && (
          <div className="mt-3 text-sm text-muted-foreground">
            <div>
              Adăugate: <strong>{bulkSummary.added}</strong>
            </div>
            <div>
              Deja prezente: <strong>{bulkSummary.alreadyPresent}</strong>
            </div>
            <div>
              Invalide: <strong>{bulkSummary.invalid}</strong>
            </div>
          </div>
        )}
      </Card>

      {/* Blacklist Table */}
      <Card className="shadow-sm hover:shadow-md transition-shadow duration-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Telefon</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Eșecuri</TableHead>
              <TableHead>Motiv</TableHead>
              <TableHead>Ultimul status</TableHead>
              <TableHead>Ultima comandă</TableHead>
              <TableHead>Creat</TableHead>
              <TableHead>Actualizat</TableHead>
              <TableHead>Acțiuni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-10">
                  Se încarcă...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center py-10 text-red-600"
                >
                  {error}
                </TableCell>
              </TableRow>
            ) : (data?.items?.length ?? 0) === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-10">
                  Nu există înregistrări
                </TableCell>
              </TableRow>
            ) : (
              data?.items.map((item) => (
                <TableRow key={item.phone}>
                  <TableCell className="font-mono">{item.phone}</TableCell>
                  <TableCell>
                    {item.isBlacklisted ? (
                      <Badge className="px-3 py-1.5 rounded-full shadow-sm ring-1 ring-inset bg-red-100 text-red-800 ring-current/20">
                        În blacklist
                      </Badge>
                    ) : (
                      <Badge className="px-3 py-1.5 rounded-full shadow-sm ring-1 ring-inset bg-green-100 text-green-800 ring-current/20">
                        Permis
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>{item.failureCount}</TableCell>
                  <TableCell
                    className="max-w-xs truncate"
                    title={item.reason || ""}
                  >
                    {item.reason || "-"}
                  </TableCell>
                  <TableCell>#{item.lastStatusId ?? "-"}</TableCell>
                  <TableCell>#{item.lastOrderId ?? "-"}</TableCell>
                  <TableCell>{formatDate(item.createdAt)}</TableCell>
                  <TableCell>{formatDate(item.updatedAt)}</TableCell>
                  <TableCell>
                    {item.isBlacklisted ? (
                      <Button
                        variant="outline"
                        className="flex items-center gap-2 hover:bg-gray-100 transition-colors duration-200"
                        onClick={() => handleRemove(item.phone)}
                        disabled={isMutatingRemove}
                      >
                        <MinusCircle className="w-4 h-4" />
                        Scoate
                      </Button>
                    ) : (
                      <Button
                        className="flex items-center gap-2 bg-red-600 hover:bg-red-700"
                        onClick={async () => {
                          try {
                            setIsMutatingAdd(true);
                            await add(item.phone);
                            await refetch();
                          } catch (e) {
                            alert(
                              e instanceof Error
                                ? e.message
                                : "Eroare la adăugare"
                            );
                          } finally {
                            setIsMutatingAdd(false);
                          }
                        }}
                        disabled={isMutatingAdd}
                      >
                        <PlusCircle className="w-4 h-4" />
                        Adaugă
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      {data && data.pagination.total > pageSize && (
        <div className="flex justify-center mt-4">
          <div className="flex gap-2">
            <Button
              className="flex items-center gap-2  transition-colors duration-200"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Anterior
            </Button>
            <div className="flex items-center gap-1">
              <span className="text-sm text-muted-foreground">
                Pagina {page} din {data.pagination.totalPages}
              </span>
            </div>
            <Button
              className="flex items-center gap-2  transition-colors duration-200"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= (data.pagination.totalPages || 1)}
            >
              Următor
            </Button>
          </div>
        </div>
      )}

      {/* ================= IP Blacklist ================= */}
      <Card className="flex justify-between items-center ">
        <div className="flex items-center gap-2">
          <Ban className="w-8 h-8 text-blue-600" />
          <div>
            <h2 className="text-2xl font-bold text-foreground">IP Blacklist</h2>
            <div className="flex items-center gap-2 mt-1">
              <AlertCircle className="w-4 h-4 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Gestionează IP-urile blocate.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* IP Filters */}
      <Card className="p-6 ">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">Filtre IP</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Caută după IP..."
              value={ipSearch}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setIpSearch(e.target.value)
              }
            />
          </div>

          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="flex flex-col gap-1">
              <Input
                placeholder="IP (IPv4 sau IPv6)"
                value={newIp}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value.trim();
                  setNewIp(value);
                  setIpError(
                    value.length > 0 &&
                      (isValidIPv4(value) || isValidIPv6(value))
                      ? null
                      : "IP invalid (acceptă IPv4/IPv6)"
                  );
                }}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter" && isValidIp) handleIpAdd();
                }}
              />
              {ipError && (
                <span className="text-xs text-red-600">{ipError}</span>
              )}
            </div>
            <Input
              placeholder="Motiv (opțional)"
              value={newIpReason}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setNewIpReason(e.target.value)
              }
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "Enter" && isValidIp) handleIpAdd();
              }}
            />
            <Button
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
              onClick={handleIpAdd}
              disabled={isMutatingIpAdd || !isValidIp}
            >
              <PlusCircle className="w-4 h-4" />
              Adaugă IP în blacklist
            </Button>
          </div>
        </div>
      </Card>

      {/* IP Bulk Import */}
      <Card className="p-6 ">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">
            Import din Excel (prima coloană = IP)
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
          <div className="flex flex-col gap-1">
            <Input
              placeholder="Importă din Excel"
              className="flex items-center gap-2 text-muted-foreground "
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setIpBulkFile(e.target.files?.[0] || null)}
            />
            {ipBulkFile && (
              <span className="text-xs text-muted-foreground">
                {ipBulkFile.name}
              </span>
            )}
          </div>
          <Input
            placeholder="Motiv pentru toate (opțional)"
            value={ipBulkReason}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setIpBulkReason(e.target.value)
            }
          />
          <Button
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            onClick={handleIpBulkImport}
            disabled={ipBulkLoading || !ipBulkFile}
          >
            {ipBulkLoading ? "Se procesează..." : "Importă IP-uri din Excel"}
          </Button>
        </div>
        {ipBulkSummary && (
          <div className="mt-3 text-sm text-muted-foreground">
            <div>
              Adăugate: <strong>{ipBulkSummary.added}</strong>
            </div>
            <div>
              Deja prezente: <strong>{ipBulkSummary.alreadyPresent}</strong>
            </div>
            <div>
              Invalide: <strong>{ipBulkSummary.invalid}</strong>
            </div>
          </div>
        )}
      </Card>

      {/* IP Blacklist Table */}
      <Card className="shadow-sm hover:shadow-md transition-shadow duration-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>IP</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Motiv</TableHead>
              <TableHead>Creat</TableHead>
              <TableHead>Actualizat</TableHead>
              <TableHead>Acțiuni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ipIsLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10">
                  Se încarcă...
                </TableCell>
              </TableRow>
            ) : ipListError ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center py-10 text-red-600"
                >
                  {ipListError}
                </TableCell>
              </TableRow>
            ) : (ipData?.items?.length ?? 0) === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10">
                  Nu există înregistrări
                </TableCell>
              </TableRow>
            ) : (
              ipData?.items.map((item) => (
                <TableRow key={item.ip}>
                  <TableCell className="font-mono">{item.ip}</TableCell>
                  <TableCell>
                    {item.isBlacklisted ? (
                      <Badge className="px-3 py-1.5 rounded-full shadow-sm ring-1 ring-inset bg-red-100 text-red-800 ring-current/20">
                        În blacklist
                      </Badge>
                    ) : (
                      <Badge className="px-3 py-1.5 rounded-full shadow-sm ring-1 ring-inset bg-green-100 text-green-800 ring-current/20">
                        Permis
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell
                    className="max-w-xs truncate"
                    title={item.reason || ""}
                  >
                    {item.reason || "-"}
                  </TableCell>
                  <TableCell>{formatDate(item.createdAt)}</TableCell>
                  <TableCell>{formatDate(item.updatedAt)}</TableCell>
                  <TableCell>
                    {item.isBlacklisted ? (
                      <Button
                        variant="outline"
                        className="flex items-center gap-2 hover:bg-gray-100 transition-colors duration-200"
                        onClick={() => handleIpRemove(item.ip)}
                        disabled={isMutatingIpRemove}
                      >
                        <MinusCircle className="w-4 h-4" />
                        Scoate
                      </Button>
                    ) : (
                      <Button
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                        onClick={async () => {
                          try {
                            setIsMutatingIpAdd(true);
                            await addIp(item.ip);
                            await ipRefetch();
                          } catch (e) {
                            alert(
                              e instanceof Error
                                ? e.message
                                : "Eroare la adăugare"
                            );
                          } finally {
                            setIsMutatingIpAdd(false);
                          }
                        }}
                        disabled={isMutatingIpAdd}
                      >
                        <PlusCircle className="w-4 h-4" />
                        Adaugă
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* IP Pagination */}
      {ipData && ipData.pagination.total > ipPageSize && (
        <div className="flex justify-center mt-4">
          <div className="flex gap-2">
            <Button
              className="flex items-center gap-2  transition-colors duration-200"
              onClick={() => setIpPage((p) => Math.max(1, p - 1))}
              disabled={ipPage === 1}
            >
              Anterior
            </Button>
            <div className="flex items-center gap-1">
              <span className="text-sm text-muted-foreground">
                Pagina {ipPage} din {ipData.pagination.totalPages}
              </span>
            </div>
            <Button
              className="flex items-center gap-2  transition-colors duration-200"
              onClick={() => setIpPage((p) => p + 1)}
              disabled={ipPage >= (ipData.pagination.totalPages || 1)}
            >
              Următor
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
